/**
 * Copyright (c) 2026, CYBRA Corp.
 */
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import {
    DataMappingService,
    DataMappingHeaderDto
} from 'app/services/data-mapping.service';

import {
    DataMappingUploadService, UploadJobStatusDto
} from 'app/services/data-mapping-upload.service';
import {
    ERROR_DIALOG_TITLE,
    SimpleSuccessErrorDialogComponent
} from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';


@Component({
    selector: 'app-data-mapping-upload',
    templateUrl: './data-mapping-upload.component.html'
})
export class DataMappingUploadComponent implements OnInit, OnDestroy {

    /* ===================== UI STATE ===================== */

    headers: DataMappingHeaderDto[] = [];
    selectedHeaderId?: number;
    selectedFile?: File;

    isUploading = false;

    jobId?: string;
    jobStatus?: UploadJobStatusDto;

    private pollTimer?: number;

    @ViewChild('successErrorDialog', { static: false })
    successErrorDialog!: SimpleSuccessErrorDialogComponent;
    /* ===================== CONSTRUCTOR ===================== */

    constructor(
        private headerService: DataMappingService,
        private uploadService: DataMappingUploadService
    ) { }

    /* ===================== INIT / DESTROY ===================== */

    async ngOnInit(): Promise<void> {
        this.headers = await this.headerService.getAllHeaders();
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    /* ===================== FILE SELECTION ===================== */

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) {
            this.selectedFile = undefined;
            return;
        }

        // STRICT: extension check ONLY
        const isCsv = file.name.toLowerCase().endsWith('.csv');

        if (!isCsv) {
            this.showError('Only CSV files are allowed');
            input.value = '';            // reset chooser
            this.selectedFile = undefined;
            return;
        }

        this.selectedFile = file;
    }

    /* ===================== UPLOAD ===================== */

    async upload(): Promise<void> {

        if (!this.selectedHeaderId) {
            this.showError('Please select a Data Mapping');
            return;
        }

        if (!this.selectedFile) {
            this.showError('Please select a CSV File');
            return;
        }

        this.isUploading = true;
        this.jobStatus = undefined;
        this.jobId = undefined;

        try {
            // Start async upload
            const response = await this.uploadService.upload(
                this.selectedHeaderId,
                this.selectedFile
            );

            // Backend returns: { jobId: "uuid" }
            this.jobId = (response as any).jobId;

            // Begin polling
            this.startPolling();

        } catch (err) {
            this.showError(err);
            this.isUploading = false;
        }
    }

    /* ===================== POLLING ===================== */

    private startPolling(): void {

        if (!this.jobId) {
            return;
        }

        this.pollTimer = window.setInterval(async () => {
            try {
                this.jobStatus =
                    await this.uploadService.getStatus(this.jobId!);

                if (
                    this.jobStatus.status === 'COMPLETED' ||
                    this.jobStatus.status === 'FAILED'
                ) {
                    this.stopPolling();
                    this.isUploading = false;
                }

            } catch (err) {
                this.showError(err);
                this.stopPolling();
                this.isUploading = false;
            }
        }, 1000);
    }

    private stopPolling(): void {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = undefined;
        }
    }

    private showError(err: any): void {
        this.successErrorDialog.showErrorDialog(
            ERROR_DIALOG_TITLE,
            err?.error || err?.message || err || 'Unexpected error occurred'
        );
    }

}
